import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, HardDrive, Server, ScanSearch, Wifi,
  ArrowRight, Lock, Mail, CheckCircle2, Zap, Code2, Search, MapPin, Smartphone, Gauge, Layers, Camera, Cpu, Scale,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { SectionHeading, Eyebrow } from "@/components/Section";
import { SEO, ORGANIZATION_JSONLD } from "@/components/SEO";
import { PartnersStrip } from "@/components/PartnersStrip";
import { HomeStory } from "@/components/home/HomeStory";

const TILE_ICONS = {
  "local-ai": Cpu,
  "managed-services": Layers,
  "managed-security": ShieldCheck,
  "managed-backup": HardDrive,
  "managed-server": Server,
  "schwachstellen": ScanSearch,
  "managed-wlan": Wifi,
  "cameras": Camera,
  "webdesign": Code2,
  "website-check": Gauge,
};

const TILE_IMAGES = {
  "local-ai": "/wp-images/lokale-ki-tile.jpg",
  "managed-services": "/wp-images/managed-services-tile.jpg",
  "managed-security": "/wp-images/managed-security.jpg",
  "managed-backup": "/wp-images/managed-backup.jpg",
  "managed-server": "/wp-images/managed-server.jpg",
  "schwachstellen": "/wp-images/schwachstellenmanagement.jpg",
  "managed-wlan": "/wp-images/wlan-basic.jpg",
  "cameras": "/wp-images/ueberwachungskamera-4g.jpg",
  "webdesign": "/wp-images/webdesign-tile.jpg",
  "website-check": "/wp-images/webseiten-check-tile.jpg",
};

export const Home = () => {
  const { t, lang } = useI18n();

  return (
    <div data-testid="home-page" className="relative">
      <SEO
        title={lang === "de" ? "IT-Dienstleister & IT-Service in Hessen · IT-Sicherheit für den Mittelstand" : "IT Service Provider & IT Consulting from Germany"}
        description={lang === "de"
          ? "IT-Servicepoint – Ihr IT-Dienstleister in Hessen: IT-Service mit SLA, IT-Beratung, Managed Security, lokale DSGVO-konforme KI, Backup & Server Hosting für den Mittelstand. 10+ Jahre Erfahrung, made in Germany. Jetzt kostenfreies Erstgespräch."
          : "IT-Servicepoint – your IT service provider in Germany: managed IT services, IT consulting, security, local GDPR-compliant AI, backup and server hosting for SMBs. 10+ years experience, made in Germany."}
        jsonLd={ORGANIZATION_JSONLD}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 pb-24 sm:pt-24 sm:pb-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 animate-fade-up">
              <Eyebrow>{t.home.eyebrow}</Eyebrow>
              <h1 className="mt-6 font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tightest leading-[0.9] text-ink">
                {t.home.headline_a}
                <span className="block text-primary">{t.home.headline_b}</span>
              </h1>
              <p className="mt-8 max-w-2xl text-lg sm:text-xl text-muted leading-relaxed">
                {t.home.sub}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/kontakt"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-medium hover:bg-primary-hover transition-colors shadow-soft"
                >
                  {t.cta.get_started}
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href="tel:+4966299153890"
                  data-testid="hero-cta-call"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-line bg-white text-ink hover:border-primary hover:text-primary transition-colors font-medium"
                >
                  +49 (0) 6629 9153890
                </a>
              </div>
            </div>
            <div className="lg:col-span-4 space-y-3">
              <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-muted">
                <span className="w-2 h-2 bg-accent animate-blink" />
                Live · 24/7 SOC Monitoring
              </div>
              <div className="border border-line bg-white p-6 font-mono text-xs space-y-2 shadow-soft">
                <div className="flex justify-between"><span className="text-muted">$ status</span><span className="text-accent font-semibold">running</span></div>
                <div className="flex justify-between"><span className="text-muted">$ firewall</span><span className="text-primary font-semibold">SECUREPOINT UTM</span></div>
                <div className="flex justify-between"><span className="text-muted">$ av_pro</span><span className="text-ink font-semibold">ACTIVE · 24/7</span></div>
                <div className="flex justify-between"><span className="text-muted">$ backup</span><span className="text-ink font-semibold">AIR-GAP · DE</span></div>
                <div className="flex justify-between"><span className="text-muted">$ awareness</span><span className="text-ink font-semibold">PLUS</span></div>
                <div className="flex justify-between"><span className="text-muted">$ uptime</span><span className="text-cyan font-semibold">99.98%</span></div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-line bg-white">
            {t.home.stats.map((s) => (
              <div key={s.label} className="border-r border-b border-line p-6 sm:p-8">
                <div className="font-display text-4xl sm:text-5xl font-black tracking-tightest text-primary">{s.value}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SLOGAN BANNER (Über uns) */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/wp-images/hero-main.jpg"
            alt="IT-Servicepoint Team und Infrastruktur in Hessen"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/70 to-primary/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <div className="max-w-2xl">
            <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-accent">[ Über uns ]</span>
            <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-white">
              Erstklassige Lösungen für Ihre IT-Sicherheit und Effizienz.
            </h2>
            <p className="mt-6 text-white/85 text-lg leading-relaxed">
              Als Anbieter in der IT-Sicherheit mit über 10 Jahren Expertise bieten wir
              professionelle und zertifizierte Lösungen für den deutschen Mittelstand.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+4966299153890"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
              >
                Jetzt anrufen
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/40 text-white hover:bg-white/10 transition-colors"
              >
                Beratung anfragen
              </Link>
            </div>
          </div>
        </div>
      </section>

      <HomeStory />

      {/* LOKALE KI – Featured neues Kernthema, direkt unter der Praxis-Story */}
      <section className="bg-soft border-b border-line" data-testid="home-localai-teaser">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
                <span className="bg-accent text-ink px-2 py-1">Neu</span>
                <span className="text-muted">Lokale KI · 100 % im eigenen Haus</span>
              </div>
              <h2 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.0] text-ink">
                Ihre eigene KI –<br />
                <span className="text-primary">Ihre Daten bleiben da.</span>
              </h2>
              <p className="mt-6 max-w-xl text-muted text-lg leading-relaxed">
                Genau wie die Praxis oben nutzen Kanzleien, Steuerbüros und Arztpraxen zunehmend KI – aber vertrauliche Daten gehören nicht in fremde Clouds. Wir bauen eine <strong className="text-ink">DSGVO-konforme KI direkt in Ihrem Unternehmen</strong>: offlinefähig, auf Ihr Wissen trainiert, Berufsgeheimnis gewahrt.
              </p>
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                  { icon: Lock, label: "Daten verlassen nie das Haus" },
                  { icon: Scale, label: "DSGVO & § 203 StGB konform" },
                  { icon: Cpu, label: "Läuft auf eigener Hardware" },
                  { icon: CheckCircle2, label: "Trainiert auf Ihr Fachwissen" },
                ].map((it) => (
                  <li key={it.label} className="flex items-start gap-2 text-ink text-sm">
                    <it.icon size={16} className="text-primary shrink-0 mt-0.5" /> {it.label}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/lokale-ki"
                  data-testid="home-localai-cta"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white font-medium hover:bg-primary-hover transition-colors"
                >
                  Lokale KI entdecken
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-line bg-white text-ink hover:border-primary hover:text-primary transition-colors"
                >
                  Kostenfreie KI-Beratung
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative overflow-hidden border border-line shadow-soft">
                <img
                  src="/wp-images/lokale-ki-hero.jpg"
                  alt="Lokaler KI-Server im eigenen Unternehmen – DSGVO-konforme KI ohne Cloud"
                  className="w-full h-full object-cover aspect-[16/11]"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 bg-ink/85 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Keine Cloud · Made in Germany
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-line py-6 overflow-hidden bg-soft">
        <div className="marquee-mask flex animate-marquee whitespace-nowrap font-mono text-xs uppercase tracking-[0.3em] text-muted">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex shrink-0 gap-12 px-6">
              <span>· Securepoint Partner</span>
              <span>· Greenbone Certified</span>
              <span>· Acronis Backup</span>
              <span>· 24/7 SOC Monitoring</span>
              <span>· ISO 27001 Methodik</span>
              <span>· Made in Germany</span>
              <span>· Hessen · Schenklengsfeld</span>
            </div>
          ))}
        </div>
      </section>

      <PartnersStrip />

      {/* SERVICES – Kachelübersicht aller Leistungen */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading
            eyebrow={t.home.services_eyebrow}
            title={t.home.services_title}
            sub={t.home.services_sub}
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-line">
            {t.service_tiles.map((s, i) => {
              const Icon = TILE_ICONS[s.key] || ShieldCheck;
              return (
                <Link
                  key={s.key}
                  to={s.href}
                  data-testid={`home-tile-${s.key}`}
                  className="group border-r border-b border-line bg-white hover:bg-soft transition-colors overflow-hidden flex flex-col"
                >
                  <div className="aspect-[16/9] overflow-hidden border-b border-line relative">
                    <img
                      src={TILE_IMAGES[s.key]}
                      alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <span className="absolute top-3 right-3 bg-ink/75 text-white px-2.5 py-1 font-mono text-[10px] tracking-[0.2em]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 shrink-0 bg-soft border border-line flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                        <Icon size={20} />
                      </div>
                      <h3 className="font-display font-bold text-xl text-ink group-hover:text-primary transition-colors leading-tight">{s.title}</h3>
                    </div>
                    <p className="mt-4 text-muted text-sm leading-relaxed flex-1">{s.desc}</p>
                    <div className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
                      {t.cta.learn_more} <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUE PROP / BENTO */}
      <section className="bg-soft border-y border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading eyebrow={t.home.values_eyebrow} title={t.home.values_title} sub={t.home.values_sub} />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-7 bg-white border border-line p-10 relative overflow-hidden min-h-[320px] shadow-soft">
              <div className="absolute right-0 bottom-0 opacity-5 pointer-events-none text-primary">
                <Lock size={280} strokeWidth={0.5} />
              </div>
              <Eyebrow>01 · Threat Response</Eyebrow>
              <h3 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink">
                Wir reagieren bevor es weh tut.
              </h3>
              <p className="mt-4 text-muted max-w-lg">
                Anomalie-Detection in Echtzeit, automatisierte Patch-Zyklen und Incident Response
                durch zertifizierte Spezialisten – damit aus Angriffsversuchen keine Schadensfälle werden.
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                {["Anomalie-Detection 24/7", "EDR mit Verhaltensanalyse", "Sofortige Eskalation", "Forensik & Reporting"].map((p) => (
                  <li key={p} className="flex items-center gap-2 text-ink"><CheckCircle2 size={16} className="text-accent" />{p}</li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-5 bg-primary text-white border border-primary p-10 min-h-[320px] flex flex-col shadow-glow">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">[ 02 · Recovery ]</span>
              <h3 className="mt-4 font-display text-3xl font-bold tracking-tight">Backup, das wirklich wiederherstellt.</h3>
              <p className="mt-4 text-white/80">
                Unified Backup mit Active Protection, Air-Gap und Notfall-Restore-Plänen.
                Damit Daten kein Glücksspiel sind.
              </p>
              <div className="mt-auto pt-8 grid grid-cols-2 gap-4">
                <div><div className="font-display text-3xl font-black text-accent">{"<15min"}</div><div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">Restore Time</div></div>
                <div><div className="font-display text-3xl font-black text-accent">3-2-1</div><div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">Backup-Regel</div></div>
              </div>
            </div>
            <div className="md:col-span-4 bg-white border border-line p-10 min-h-[260px]">
              <Eyebrow>03 · Compliance</Eyebrow>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-ink">NIS-2 & DSGVO konform.</h3>
              <p className="mt-3 text-muted text-sm">Wir bringen Sie auf den Stand – mit Dokumentation, Audits und Schulungen.</p>
            </div>
            <div className="md:col-span-4 bg-white border border-line p-10 min-h-[260px] relative">
              <Eyebrow>04 · Awareness</Eyebrow>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-ink">Menschen zur Human Firewall.</h3>
              <p className="mt-3 text-muted text-sm">Regelmäßige Trainings & Phishing-Simulationen mit messbarem Erfolg.</p>
            </div>
            <div className="md:col-span-4 bg-white border border-line p-10 min-h-[260px]">
              <Eyebrow>05 · Made in Germany</Eyebrow>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-ink">Daten bleiben hier.</h3>
              <p className="mt-3 text-muted text-sm">Sämtliche Hosting- und Cloud-Services laufen ausschließlich in deutschen Rechenzentren.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MANAGED SERVICES SHOWCASE */}
      <section className="relative overflow-hidden bg-white border-y border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-8">
              <Eyebrow>Neu · Managed Services</Eyebrow>
              <h2 className="mt-4 font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.98] text-ink">
                5 Services.<br />
                <span className="text-primary">1 Partner. 0 Kompromisse.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-muted text-lg leading-relaxed">
                Firewall, Antivirus, XDR, Backup und E-Mail-Archivierung als monatlich abgerechneter Rundum-Service. <strong className="text-ink">Made in Germany.</strong>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { num: "01", icon: ShieldCheck, name: "Managed Firewall", sub: "Securepoint UTM · 24/7" },
              { num: "02", icon: ShieldCheck, name: "Antivirus Pro", sub: "Mit Application Control" },
              { num: "03", icon: ScanSearch, name: "Managed XDR", sub: "24/7 Detection & Response" },
              { num: "04", icon: HardDrive, name: "Managed Backup", sub: "Air-Gap · Restore < 15 min" },
              { num: "05", icon: Mail, name: "Exchange & Archiv", sub: "GoBD-konform · MailStore" },
            ].map((s, i) => (
              <Link
                key={s.num}
                to={`/managed-services#${["managed-firewall","managed-antivirus-pro","managed-xdr","managed-backup","managed-email-exchange"][i]}`}
                data-testid={`home-ms-card-${i}`}
                className="group block border border-line bg-white p-6 lift"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                    <s.icon size={20} />
                  </div>
                  <span className="font-mono text-xs text-muted">{s.num}</span>
                </div>
                <h3 className="mt-6 font-display font-bold text-lg text-ink group-hover:text-primary transition-colors leading-tight">{s.name}</h3>
                <p className="mt-2 text-muted text-xs">{s.sub}</p>
              </Link>
            ))}
          </div>

          <div className="mt-14 flex flex-col sm:flex-row gap-4 items-center justify-between border border-line bg-soft p-6 sm:p-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Kostenfreies Erstgespräch</div>
              <div className="mt-2 font-display font-bold text-xl text-ink">30 Minuten. Unverbindlich. Ohne Verkaufsdruck.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link
                to="/managed-services"
                data-testid="home-ms-cta"
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white font-medium hover:bg-primary-hover transition-colors"
              >
                Alle Services entdecken
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-line bg-white text-ink hover:border-primary hover:text-primary transition-colors"
              >
                Beratung anfragen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PARALLAX BANNER (WP-style cover) */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/wp-images/schwachstellen-banner.jpg"
            alt="IT-Schwachstellenmanagement"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-ink/55" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
          <p className="font-display font-black text-3xl sm:text-5xl text-white tracking-tightest leading-tight max-w-3xl mx-auto">
            Jetzt IT-Schwachstellenmanagement planen und durchführen.
          </p>
          <div className="mt-8">
            <Link
              to="/schwachstellenmanagement"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-primary transition-colors font-medium"
            >
              Erfahren Sie mehr
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* THREATS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading eyebrow={t.home.threats_eyebrow} title={t.home.threats_title} />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-line">
            {t.threats.map((th, i) => (
              <div key={th.title} className="border-r border-b border-line p-8 bg-white hover:bg-soft transition-colors">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-soft border border-line flex items-center justify-center text-accent">
                    <Zap size={18} />
                  </div>
                  <span className="font-mono text-xs text-muted">{String(i + 1).padStart(2, "0")} / {String(t.threats.length).padStart(2, "0")}</span>
                </div>
                <h4 className="mt-6 font-display text-xl font-bold text-ink">{th.title}</h4>
                <p className="mt-3 text-muted text-sm leading-relaxed">{th.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WEBSITE CHECK WIDGET */}
      <section className="relative overflow-hidden bg-ink text-white border-b border-line">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: "radial-gradient(ellipse at 20% 20%, rgba(29,169,218,0.35), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(164,197,57,0.25), transparent 55%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
              <Gauge size={14} /> Kostenlos · 60 Sekunden · keine Anmeldung
            </div>
            <h2 className="mt-5 font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.02]">
              Wie steht Ihre Webseite<br />
              bei <span className="text-accent">Google</span> da?
            </h2>
            <p className="mt-6 max-w-xl text-white/75 text-lg leading-relaxed">
              Performance, SEO, Mobile, HTTPS und Meta-Tags – wir checken 12 Faktoren in Echtzeit und schicken Ihnen einen persönlichen Detail-Report per E-Mail.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
              {["Score 0–100 + Note A–F", "Konkrete Handlungsempfehlungen", "Report per E-Mail", "Server in Deutschland"].map((it) => (
                <li key={it} className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle2 size={16} className="text-accent shrink-0" /> {it}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/15 bg-white/5 backdrop-blur p-8 text-center">
              <div className="font-display font-black text-7xl text-accent leading-none">60s</div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">Dauer Ihres Checks</div>
              <Link
                to="/webseiten-check"
                data-testid="home-check-cta"
                className="mt-8 group inline-flex w-full items-center justify-center gap-2 px-7 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
              >
                Webseite jetzt prüfen
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <p className="mt-4 text-xs text-white/60">Keine Kreditkarte · Kein Abo · 100 % kostenfrei</p>
            </div>
          </div>
        </div>
      </section>


      {/* CTA BLOCK */}
      <section className="relative overflow-hidden bg-primary text-white">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top right, rgba(29,169,218,0.4), transparent 60%), radial-gradient(ellipse at bottom left, rgba(164,197,57,0.25), transparent 60%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32 text-center">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-accent">[ Kontakt ]</span>
          <h2 className="mt-6 font-display font-black text-4xl sm:text-6xl tracking-tightest text-white max-w-3xl mx-auto leading-[1.02]">
            {t.home.cta_block_title}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 text-lg">{t.home.cta_block_sub}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="cta-block-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              {t.cta.get_started}
              <ArrowRight size={18} />
            </Link>
            <a
              href="mailto:info@it-servicepoint.eu"
              data-testid="cta-block-email"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors font-medium"
            >
              <Mail size={18} /> info@it-servicepoint.eu
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
